import { useCallback, useEffect, useMemo, useState, SyntheticEvent } from 'react';
import { Box, Typography, Tabs, Tab } from '@mui/material';
import { useParams, useSearchParams } from 'react-router-dom';

import GradientCircularProgress from '../../../../components/common/GradientCircularProgress';
import useBackendApi from '../../../../hooks/useBackendApi';

import { useThemeContext } from '../../../../contexts/BaseThemeContextProvider';
import QuestionnairesTable from '../../../../frost/QuestionnairesTable';
import VitalsTab from '../../../../frost/VitalsTab';

const backendURL = process.env.REACT_APP_BACKEND_URL as string;

type PatientSummary = {
  user_id: string;
  full_name?: string | null;
  dob?: string | null;
  phone?: string | null;
  email?: string | null;
};

type PatientDetailsResponse = {
  patient?: PatientSummary | null;
  message?: string;
};

type RouteParams = {
  org_path_name: string;
  acc_path_name: string;
  patient_user_id: string;
};

export default function AccountEventPatientDetails(): JSX.Element {
  const { apiFetchRequest, isLoading } = useBackendApi();
  const { theme } = useThemeContext();

  const { org_path_name, acc_path_name, patient_user_id } = useParams<RouteParams>();
  const [searchParams] = useSearchParams();
  const patientUserId = useMemo(() => (patient_user_id || '').trim(), [patient_user_id]);

  const [data, setData] = useState<PatientDetailsResponse | null>(null);
  const [errorText, setErrorText] = useState<string | null>(null);

  const initialTab = searchParams.get('tab') === 'vitals' ? 1 : 0;
  const [currentTab, setCurrentTab] = useState(initialTab);

  useEffect(() => {
    setCurrentTab(searchParams.get('tab') === 'vitals' ? 1 : 0);
  }, [searchParams]);

  const handleTabChange = (_event: SyntheticEvent, newValue: number) => {
    setCurrentTab(newValue);
  };

  const fail = () => setErrorText('Unable to load patient details. Please try again.');

  const fetchPatientDetails = useCallback(async () => {
    if (!org_path_name || !acc_path_name || !patientUserId) return;

    setErrorText(null);
    setData(null);

    try {
      const endpoint = `${backendURL}/o/${encodeURIComponent(org_path_name)}/a/${encodeURIComponent(
        acc_path_name,
      )}/patients/${encodeURIComponent(patientUserId)}`;

      const res = await apiFetchRequest({ endpoint, method: 'GET' });
      if (!res) return fail();

      const { status, body, isError } = res;

      const unauthorized =
        status === 401 ||
        body?.status_code === 401 ||
        body?.message === 'UNAUTHENTICATED' ||
        body?.message === 'UNAUTHORIZED' ||
        body?.message === 'NO_ACCESS';

      if (unauthorized) {
        window.location.href = '/401.html';
        return;
      }

      const patientNotFound =
        status === 404 ||
        body?.status_code === 404 ||
        body?.detail === 'Patient not found' ||
        body?.message === 'PATIENT_NOT_FOUND';

      if (patientNotFound) {
        setErrorText('Patient not found.');
        return;
      }

      if (isError || status !== 200) return fail();

      setData(body as PatientDetailsResponse);
    } catch {
      fail();
    }
  }, [acc_path_name, apiFetchRequest, org_path_name, patientUserId]);

  useEffect(() => {
    fetchPatientDetails();
  }, [fetchPatientDetails]);

  return (
    <Box
      sx={{
        backgroundColor: theme.layout.defaults.backgroundColor,
        borderRadius: '16px',
        padding: { sm: '28px', xs: '20px' },
      }}
    >
      <Typography variant="h6" sx={{ mb: 3 }}>
        Patient Details
      </Typography>

      {isLoading && !data && !errorText ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <GradientCircularProgress />
        </Box>
      ) : null}

      {errorText ? (
        <Box sx={{ py: 2 }}>
          <Typography color="error">{errorText}</Typography>
        </Box>
      ) : null}

      {!errorText && data ? (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
          <Box
            sx={{
              backgroundColor: theme.custom.pages.frost.authHome.cards.backgroundColor,
              borderRadius: '16px',
              padding: { xs: '20px', sm: '28px' },
              border: '1px solid',
              borderColor: theme.custom.pages.frost.authHome.cards.borderColor,
            }}
          >
            <Typography
              variant="h6"
              sx={{
                fontWeight: 700,
                color: theme.custom.pages.frost.authHome.typography.body1.color,
                marginBottom: '16px',
              }}
            >
              Patient Summary
            </Typography>

            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
                columnGap: '24px',
                rowGap: '18px',
              }}
            >
              <Box>
                <Typography
                  variant="caption"
                  sx={{ fontWeight: 600, color: theme.custom.pages.frost.authHome.typography.body2.color }}
                >
                  Name
                </Typography>
                <Typography
                  sx={{
                    marginTop: '4px',
                    fontWeight: 400,
                    color: theme.custom.pages.frost.authHome.typography.body1.color,
                  }}
                >
                  {data.patient?.full_name ?? '-'}
                </Typography>
              </Box>

              <Box>
                <Typography
                  variant="caption"
                  sx={{ fontWeight: 600, color: theme.custom.pages.frost.authHome.typography.body2.color }}
                >
                  Email
                </Typography>
                <Typography
                  sx={{
                    marginTop: '4px',
                    fontWeight: 400,
                    color: theme.custom.pages.frost.authHome.typography.body1.color,
                    wordBreak: 'break-word',
                  }}
                >
                  {data.patient?.email ?? '-'}
                </Typography>
              </Box>

              <Box>
                <Typography
                  variant="caption"
                  sx={{ fontWeight: 600, color: theme.custom.pages.frost.authHome.typography.body2.color }}
                >
                  Date of Birth
                </Typography>
                <Typography
                  sx={{
                    marginTop: '4px',
                    fontWeight: 400,
                    color: theme.custom.pages.frost.authHome.typography.body1.color,
                  }}
                >
                  {data.patient?.dob
                    ? new Intl.DateTimeFormat(undefined, { year: 'numeric', month: 'long', day: 'numeric' }).format(
                        new Date(`${data.patient.dob}T00:00:00`),
                      )
                    : '-'}
                </Typography>
              </Box>

              <Box>
                <Typography
                  variant="caption"
                  sx={{ fontWeight: 400, color: theme.custom.pages.frost.authHome.typography.body2.color }}
                >
                  Phone
                </Typography>
                <Typography
                  sx={{
                    marginTop: '4px',
                    fontWeight: 500,
                    color: theme.custom.pages.frost.authHome.typography.body1.color,
                  }}
                >
                  {data.patient?.phone ?? '-'}
                </Typography>
              </Box>
            </Box>
          </Box>

          <Box sx={{ width: '100%' }}>
            <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
              <Tabs value={currentTab} onChange={handleTabChange} aria-label="patient sub-tabs">
                <Tab label="Questionnaires" />
                <Tab label="Vitals" />
              </Tabs>
            </Box>

            {currentTab === 0 && <QuestionnairesTable />}

            {currentTab === 1 && (
              <VitalsTab orgPathName={org_path_name || ''} accPathName={acc_path_name || ''} patientUserId={patientUserId} />
            )}
          </Box>
        </Box>
      ) : null}
    </Box>
  );
}
